import { groupNamePresenter, taskNamePresenter } from '@kaiizen/kai-presenters';
import type {
  ScanGroupOptimized,
  ScanPartContext,
  ScanPartOptimized,
  ScanPhoto,
  ScanRootOptimized,
} from '@kaiizen/kai-scan-template-system';

/**
 * The Photo Manager's section model (KAIDEV-3647).
 *
 * One flat list of sections per open scope, mirroring the mockup's `openPhotoManager`:
 * a task opens on its own section with its group below as context; a group lists itself then its
 * tasks; the whole project leads with Curbside (read-only context, hidden when empty) and always
 * ends in Unsorted — the scan-root part, the data model's one bucket for a photo that belongs to
 * no group or task. Every section that accepts photos carries the `scanPartContext` an upload or
 * move landing there would take, so the section you drop into IS the destination — there is no
 * picker.
 */

export type PhotoManagerScope = {
  groupId?: string;
  taskId?: string;
};

export type PhotoManagerSection = {
  key: string;
  kind: 'curbside' | 'group' | 'task' | 'unsorted';
  title: string;
  scanPart: ScanPartOptimized;
  scanPartContext: ScanPartContext;
  photos: readonly ScanPhoto[];
  acceptsPhotos: boolean;
};

export type PhotoManagerModel = {
  title: string;
  sections: readonly PhotoManagerSection[];
  photoCount: number;
};

const CURBSIDE_TITLE = 'Curbside';
const UNSORTED_TITLE = 'Unsorted';
const ALL_PHOTOS_TITLE = 'All photos';

/**
 * Newest first, undated last. A photo with no `addedAt` keeps its seed order at the end of its
 * section rather than jumping to the top — sinking is where you least want the thing just added,
 * but inventing a date for it would be worse.
 */
export const sortPhotosNewestFirst = (photos: readonly ScanPhoto[]): readonly ScanPhoto[] =>
  photos
    .map((photo, index) => ({ photo, index }))
    .sort((first, second) => {
      const firstAt = first.photo.addedAt ?? NaN;
      const secondAt = second.photo.addedAt ?? NaN;
      if (isNaN(firstAt) && isNaN(secondAt)) return first.index - second.index;
      if (isNaN(firstAt)) return 1;
      if (isNaN(secondAt)) return -1;
      return secondAt - firstAt || first.index - second.index;
    })
    .map(entry => entry.photo);

const buildTaskSection = (groupId: string, task: ScanGroupOptimized['tasks'][number]): PhotoManagerSection => ({
  key: `task:${task.id}`,
  kind: 'task',
  title: taskNamePresenter(task),
  scanPart: task,
  scanPartContext: { groupId, taskId: task.id },
  photos: sortPhotosNewestFirst(task.photos ?? []),
  acceptsPhotos: true,
});

const buildGroupSection = (group: ScanGroupOptimized): PhotoManagerSection => ({
  key: `group:${group.id}`,
  kind: 'group',
  title: groupNamePresenter(group),
  scanPart: group,
  scanPartContext: { groupId: group.id },
  photos: sortPhotosNewestFirst(group.photos ?? []),
  acceptsPhotos: true,
});

/**
 * The scan root, which is both Curbside and Unsorted.
 *
 * There is no "unfiled" bucket in the data model — a photo belongs to the scan root, a group or a
 * task — so the two names are one part seen in two states, as the design plan settled it: the
 * scan-root section always renders at scope level as the place to file a photo you cannot place,
 * and presents as Curbside once it holds any. Building them as two sections gave Unsorted no
 * photos it could ever show: a move there writes to `scanRoot.photos`, which Curbside renders.
 */
const buildScanRootSection = (scanRoot: ScanRootOptimized): PhotoManagerSection => {
  const photos = sortPhotosNewestFirst(scanRoot.photos ?? []);
  // The kind follows the state, so an empty scan root keeps the exemption that holds it on screen.
  const kind = photos.length ? 'curbside' : 'unsorted';
  return {
    key: kind,
    kind,
    title: photos.length ? CURBSIDE_TITLE : UNSORTED_TITLE,
    scanPart: scanRoot,
    scanPartContext: {},
    photos,
    acceptsPhotos: true,
  };
};

export const buildPhotoManagerModel = ({
  scanRoot,
  groups,
  groupId,
  taskId,
}: {
  scanRoot: ScanRootOptimized;
  groups: readonly ScanGroupOptimized[];
} & PhotoManagerScope): PhotoManagerModel => {
  const selectedGroup = groupId ? groups.find(group => group.id === groupId) : undefined;
  const selectedTask = taskId ? selectedGroup?.tasks?.find(task => task.id === taskId) : undefined;

  const sections: PhotoManagerSection[] = selectedTask && selectedGroup
    ? [buildTaskSection(selectedGroup.id, selectedTask), buildGroupSection(selectedGroup)]
    : selectedGroup
    ? [buildGroupSection(selectedGroup), ...(selectedGroup.tasks ?? []).map(task => buildTaskSection(selectedGroup.id, task))]
    : [
      ...groups.flatMap(group => [
        buildGroupSection(group),
        ...(group.tasks ?? []).map(task => buildTaskSection(group.id, task)),
      ]),
      // Always last, always a destination: without it there is nowhere to file a photo you can't place.
      buildScanRootSection(scanRoot),
    ];

  const title = selectedTask
    ? taskNamePresenter(selectedTask)
    : selectedGroup
    ? groupNamePresenter(selectedGroup)
    : ALL_PHOTOS_TITLE;

  return {
    title,
    sections,
    photoCount: sections.reduce((total, section) => total + section.photos.length, 0),
  };
};
